"use client"

import type { KeyboardEvent } from "react"
import { VoiceButton } from "./VoiceButton"
import { useVoice } from "../hooks/useVoice"

interface MessageInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  isLoading: boolean
  placeholder: string
  primaryColor: string
  voiceEnabled?: boolean
}

export function MessageInput({ value, onChange, onSend, isLoading, placeholder, primaryColor, voiceEnabled = false }: MessageInputProps) {
  const { isListening, isSupported, startListening, stopListening } = useVoice({
    onTranscript: (text) => {
      console.log('MessageInput received transcript:', text)
      onChange(text) // Set the transcribed text in input field
      console.log('MessageInput onChange called with:', text)
    },
    enabled: voiceEnabled,
  })

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      onSend()
    }
  }

  const handleVoiceToggle = () => {
    console.log('Voice toggle clicked, isListening:', isListening)
    if (isListening) {
      console.log('Stopping voice recognition')
      stopListening()
    } else {
      console.log('Starting voice recognition')
      startListening()
    }
  }

  const adjustColor = (color: string, percent: number): string => {
    const num = parseInt(color.replace("#", ""), 16)
    const amt = Math.round(2.55 * percent)
    const R = (num >> 16) + amt
    const G = (num >> 8 & 0x00FF) + amt
    const B = (num & 0x0000FF) + amt
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)
  }

  return (
    <div style={{
      padding: '16px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: 'white'
    }}>
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end'
      }}>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={isLoading}
          rows={1}
          style={{
            flex: 1,
            border: '1px solid #e5e7eb',
            borderRadius: '20px',
            padding: '12px 16px',
            fontSize: '14px',
            outline: 'none',
            resize: 'none',
            overflow: 'hidden',
            minHeight: '44px',
            maxHeight: '120px',
            backgroundColor: 'white',
            color: '#1f2937',
            fontFamily: 'inherit'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = primaryColor
            e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e5e7eb'
            e.target.style.boxShadow = 'none'
          }}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement
            target.style.height = 'auto'
            target.style.height = Math.min(target.scrollHeight, 120) + 'px'
          }}
        />
        
        {/* Show voice button by default when voice enabled, or send button when typing */}
        {voiceEnabled && !value.trim() ? (
          <button
            onClick={handleVoiceToggle}
            disabled={!isSupported}
            style={{
              background: isListening ? `linear-gradient(135deg, #ef4444, #dc2626)` : `linear-gradient(135deg, ${primaryColor}, ${adjustColor(primaryColor, -10)})`,
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: !isSupported ? 'not-allowed' : 'pointer',
              opacity: !isSupported ? 0.5 : 1,
              transition: 'all 0.2s ease',
              animation: isListening ? 'pulse 1.5s infinite' : 'none'
            }}
            onMouseEnter={(e) => {
              if (isSupported) {
                e.currentTarget.style.transform = 'scale(1.05)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            aria-label={isListening ? "Stop recording" : "Start voice recording"}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
        ) : (
          <button
            onClick={onSend}
            disabled={!value.trim() || isLoading}
            style={{
              background: `linear-gradient(135deg, ${primaryColor}, ${adjustColor(primaryColor, -10)})`,
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: !value.trim() || isLoading ? 'not-allowed' : 'pointer',
              opacity: !value.trim() || isLoading ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!(!value.trim() || isLoading)) {
                e.currentTarget.style.transform = 'scale(1.05)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            aria-label="Send message"
          >
            {isLoading ? (
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid transparent',
                borderTop: '2px solid currentColor',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  )
}